
import Vue from 'vue'
import type { Post, User, Cid, CommentId, Comment } from 'types'
import { Component, Prop } from 'vue-property-decorator'
import BaseIcon from 'vuecomponents/base_icon.vue'
import { namespace, Getter, State } from 'vuex-class'
import { isAppUsing } from 'bits/flip'
import SurfacePostComment from 'vuecomponents/SurfacePostComment.vue'
import MultilineInput from 'vuecomponents/multiline_input.vue'
import AvatarImage from 'vuecomponents/avatar_image.vue'
import OzTextButton, { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'

const commentModule = namespace('comment')
const browser = namespace('browser')
const postAction = namespace('postAction')

@Component({
  components: {
    AvatarImage,
    BaseIcon,
    SurfacePostComment,
    MultilineInput,
    OzTextButton,
    OzIcon,
  },
})
export default class SurfacePostComments extends Vue {
  OzTextButtonColorScheme = OzTextButtonColorScheme
  isAddCommentFocused = false

  @Getter colorScheme!: 'light' | 'dark'

  @Prop({ default: null }) post!: Post
  @Prop({ default: null }) user!: User
  @Prop({ default: null }) commentIds!: CommentId[]
  @Prop({ default: null }) comments!: { [id: number]: Comment }
  @Prop({ default: false }) isCommentable!: boolean
  @Prop({ default: null }) commentBeingEditedId!: CommentId

  @State isApp!: boolean
  @Getter isStream!: boolean
  @Getter dir!: string

  @commentModule.Getter newCommentBodyForPostCid
  @commentModule.Action startWritingNewComment
  @commentModule.Action stopWritingNewComment
  @commentModule.Action updateNewCommentBody
  @commentModule.Action updateInlineCommentInputLastTypedInMs

  @browser.Getter isPhone!: boolean

  @postAction.Getter expandedPostCid!: Cid

  get isNewPostExpandedPanelOpen(): boolean {
    return isAppUsing('expandedPostV1') && !!this.expandedPostCid && !this.isApp
  }

  get commentsList(): Comment[] {
    return this.commentIds.map((id) => this.comments[id])
  }

  get newCommentBody(): string {
    return this.newCommentBodyForPostCid(this.post.cid)
  }

  stopEditingComment(): void {
    this.$emit('stop-editing')
  }

  editComment($event): void {
    this.$emit('edit', $event)
  }

  publishComment(): void {
    if (!this.newCommentBody) return

    this.$emit('create', {
      postId: this.post.id,
      body: this.newCommentBody,
    })
    this.updateNewCommentBody({ postCid: this.post.cid, body: '' })
    const vueInputElement = this.$refs.addCommentInput as MultilineInput
    vueInputElement.focus()
  }

  submit(): void {
    this.publishComment()
  }

  onAddCommentFocus(): void {
    this.isAddCommentFocused = true
    this.startWritingNewComment({ postId: this.post.id })
  }

  onAddCommentBlur(): void {
    this.isAddCommentFocused = false
    if (!this.isPhone) {
      // in mobile screen size, the add comment text field acts a "button" to open the add comment modal
      // we don't need to stop editing when the text input lose focus
      this.stopWritingNewComment({ postId: this.post.id })
    }
  }

  onAddCommentInput(body): void {
    this.updateNewCommentBody({ postCid: this.post.cid, body })
    this.updateInlineCommentInputLastTypedInMs()
  }
}
